import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Radio } from 'lucide-react';
import TeamLogo from './TeamLogo';
import MatchLiveStats from './MatchLiveStats';
import { shortTeamName } from './teamUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NRL_NICKNAME_MAP = {
  Broncos: 'Brisbane Broncos', Raiders: 'Canberra Raiders', Bulldogs: 'Canterbury Bulldogs',
  Sharks: 'Cronulla Sharks', Dolphins: 'Dolphins', Titans: 'Gold Coast Titans',
  'Sea Eagles': 'Manly Sea Eagles', Storm: 'Melbourne Storm', Knights: 'Newcastle Knights',
  Warriors: 'New Zealand Warriors', Cowboys: 'North Queensland Cowboys', Eels: 'Parramatta Eels',
  Panthers: 'Penrith Panthers', Rabbitohs: 'South Sydney Rabbitohs',
  Dragons: 'St George Illawarra Dragons', Roosters: 'Sydney Roosters', 'Wests Tigers': 'Wests Tigers',
};

function isLiveStatus(status) {
  const s = (status || '').toLowerCase();
  return s === 'live' || s === 'in_progress' || s === 'inprogress' ||
    s === 'firsthalf' || s === 'first_half' || s === 'secondhalf' || s === 'second_half' ||
    s === 'halftime' || s === 'half time' || s === 'half_time' || s === '1h' || s === '2h' || s === 'ht';
}

function isFinishedStatus(status) {
  const s = (status || '').toLowerCase();
  return s === 'concluded' || s === 'postgame' || s === 'post_game' || s === 'post game' ||
    s === 'fulltime' || s === 'full time' || s === 'full_time' || s === 'completed' ||
    s === 'final' || s === 'ft' || s === 'aet' || s === 'pen';
}

function shouldShowAsLive(g, sport) {
  // Hard-exclude completed games even if API has flipped back to LIVE briefly
  if (isFinishedStatus(g.status)) return false;
  if (!isLiveStatus(g.status)) return false;
  if (sport === 'AFL') {
    if (g.period_number != null && g.period_number > 4) return false;
    // AFL quarters are 20 min plus time-on (stoppage). Allow up to 35 min before
    // treating an unfinished game as stuck — the API `status` (CONCLUDED/POSTGAME)
    // is the authoritative signal of full time.
    if (g.period_number != null && g.period_seconds != null) {
      if (g.period_number === 4 && g.period_seconds >= 35 * 60) return false;
    }
  } else if (sport === 'NRL') {
    if (g.period_number != null && g.period_number > 2) return false;
    // NRL halves are 40 min plus stoppage time. Allow up to 50 min before
    // treating an unfinished game as stuck.
    if (g.period_number != null && g.period_seconds != null) {
      if (g.period_number === 2 && g.period_seconds >= 50 * 60) return false;
    }
  }
  // WC: trust API status (1H/HT/2H = live, FT/AET/PEN = finished)
  return true;
}

function computeWcMinute(startTime) {
  if (!startTime) return null;
  const koMs = new Date(startTime).getTime();
  if (isNaN(koMs)) return null;
  const elapsedMin = Math.floor((Date.now() - koMs) / 60000);
  if (elapsedMin < 0) return null;
  if (elapsedMin <= 45) return { half: 1, minute: elapsedMin };
  if (elapsedMin <= 60) return { half: 'HT', minute: null };
  const secondHalfMin = elapsedMin - 15;
  if (secondHalfMin <= 90) return { half: 2, minute: secondHalfMin };
  return { half: 2, minute: 90, extraTime: secondHalfMin - 90 };
}

function formatWcClock(game) {
  const raw = (game.status_raw || game.status || '').toUpperCase();
  if (raw === 'HT' || raw.includes('HALF TIME') || raw.includes('HALF_TIME')) return 'HT';
  if (raw.includes('ET') || raw.includes('EXTRA')) return 'ET';
  const tick = computeWcMinute(game.start_time);
  if (tick) {
    if (tick.half === 'HT') return 'HT';
    if (tick.half === 1) return `1H · ${tick.minute}'`;
    if (tick.half === 2) {
      if (tick.extraTime != null) return `2H · 90+${tick.extraTime}'`;
      return `2H · ${tick.minute}'`;
    }
  }
  if (raw.includes('1H') || raw.includes('FIRST HALF')) return '1H';
  if (raw.includes('2H') || raw.includes('SECOND HALF')) return '2H';
  return 'LIVE';
}

function formatQuarterClock(periodNumber, periodSeconds, sport) {
  if (periodNumber == null) return null;
  const half = sport === 'NRL';
  const label = half
    ? (periodNumber === 1 ? 'H1' : periodNumber === 2 ? 'H2' : `H${periodNumber}`)
    : `Q${periodNumber}`;
  if (periodSeconds == null) return label;
  const m = Math.floor(periodSeconds / 60);
  const s = Math.abs(periodSeconds) % 60;
  return `${label} · ${m}:${String(s).padStart(2, '0')}`;
}

function formatNrlClock(gameTime) {
  if (!gameTime || typeof gameTime !== 'string') return null;
  const match = gameTime.match(/^(\d{1,3}):(\d{1,2})$/);
  if (!match) return gameTime;
  const totalMins = parseInt(match[1], 10);
  const secs = parseInt(match[2], 10);
  const inSecondHalf = totalMins >= 40;
  const half = inSecondHalf ? 'H2' : 'H1';
  const minInHalf = inSecondHalf ? totalMins - 40 : totalMins;
  return `${half} · ${minInHalf}:${String(secs).padStart(2, '0')}`;
}

function leaderText(hs, as_, home, away, sport) {
  const h = hs || 0, a = as_ || 0;
  if (h === a) return null;
  const leader = h > a ? home : away;
  const diff = Math.abs(h - a);
  if (sport === 'WC') {
    return `${leader} by ${diff} ${diff === 1 ? 'goal' : 'goals'}`;
  }
  return `${shortTeamName(leader)} by ${diff}`;
}

function findTipStatusForGame(tipStatus, homeTeam, awayTeam, sport) {
  for (const [fid, data] of Object.entries(tipStatus || {})) {
    if (!fid.toLowerCase().startsWith(sport.toLowerCase())) continue;
    const teams = new Set([homeTeam, awayTeam]);
    if (data.entries?.some(e => teams.has(e.predicted_winner)) || teams.has(data.current_leader)) {
      return data;
    }
  }
  return null;
}

function LiveCard({ game, sport, tipStatus, wcLogos, eplLogos }) {
  const accent =
    sport === 'AFL' ? '#E00000' :
    sport === 'NRL' ? '#007A33' :
    sport === 'EPL' ? '#37003C' :
    '#4338CA';
  const homeName = sport === 'NRL' ? (NRL_NICKNAME_MAP[game.home_team] || game.home_team) : game.home_team;
  const awayName = sport === 'NRL' ? (NRL_NICKNAME_MAP[game.away_team] || game.away_team) : game.away_team;
  const isWC = sport === 'WC';
  const isEPL = sport === 'EPL';
  // Soccer sports (WC + EPL) share the same clock render logic.
  const isSoccer = isWC || isEPL;

  const clockText = sport === 'AFL'
    ? formatQuarterClock(game.period_number, game.period_seconds, 'AFL') || game.game_time || 'LIVE'
    : sport === 'NRL'
      ? (formatNrlClock(game.game_time) || 'LIVE')
      : formatWcClock(game);

  const leader = leaderText(game.home_score, game.away_score,
    sport === 'NRL' ? game.home_team : (isSoccer ? homeName : game.home_team),
    sport === 'NRL' ? game.away_team : (isSoccer ? awayName : game.away_team),
    sport);

  return (
    <div className="tipster-card !p-0 bg-green-50/40 border-green-200 overflow-hidden" data-testid={`live-ticker-card-${sport}-${game.home_team}`}>
      {/* Header strip */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-green-200/60">
        <div className="flex items-center gap-1.5">
          <Radio className="w-3 h-3 animate-pulse" style={{ color: accent }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accent }}>
            {isWC ? 'WORLD CUP' : sport}
          </span>
          <span
            className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm"
            style={{ backgroundColor: '#34C75920', color: '#34C759' }}
          >
            {clockText}
          </span>
        </div>
        <span className="text-[10px] text-gray-400 truncate max-w-[120px]">{game.venue}</span>
      </div>

      {/* Teams + scores */}
      <div className="flex items-center gap-3 px-3 py-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <TeamLogo teamName={homeName} logoUrl={isWC ? wcLogos?.[homeName] : (isEPL ? eplLogos?.[homeName] : undefined)} size={28} />
          <span className="font-semibold text-sm text-[#1A1A1A] truncate">
            {sport === 'NRL' ? game.home_team : shortTeamName(game.home_team)}
          </span>
        </div>
        <div className="flex flex-col items-center flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="text-lg font-black tabular-nums text-[#1A1A1A]">{game.home_score || 0}</span>
            <span className="text-xs text-gray-400">-</span>
            <span className="text-lg font-black tabular-nums text-[#1A1A1A]">{game.away_score || 0}</span>
          </div>
          {leader && (
            <span className="text-[10px] text-gray-500 mt-0.5">{leader}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          <span className="font-semibold text-sm text-[#1A1A1A] truncate text-right">
            {sport === 'NRL' ? game.away_team : shortTeamName(game.away_team)}
          </span>
          <TeamLogo teamName={awayName} logoUrl={isWC ? wcLogos?.[awayName] : (isEPL ? eplLogos?.[awayName] : undefined)} size={28} />
        </div>
      </div>

      {/* Tip status row — who's earning live points */}
      {(() => {
        const gameStatus = findTipStatusForGame(tipStatus, homeName, awayName, sport);
        if (!gameStatus?.entries?.length) return null;
        return (
          <div className="px-3 py-2 border-t border-green-200/60">
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {gameStatus.entries.map((entry) => (
                <div key={entry.user_id} className="flex items-center gap-1.5">
                  <span className={`w-4 h-4 rounded-full text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${
                    entry.is_winning ? 'bg-[#34C759]' : 'bg-[#FF3B30]'
                  }`}>
                    {entry.name.charAt(0)}
                  </span>
                  <span className={`text-[11px] font-semibold ${entry.is_winning ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                    {entry.name}
                  </span>
                  <span className={`text-[11px] font-bold ${entry.points > 0 ? 'text-[#007AFF]' : 'text-gray-400'}`}>
                    {entry.points > 0 ? `+${entry.points}` : '0'}
                  </span>
                  {entry.bonus && entry.bonus.length > 0 && (
                    <span className="text-[11px] text-[#FFB81C]" title={entry.bonus.join(', ')}>
                      {entry.bonus.map(b => b.includes('Exact') ? '!!!' : b.includes('Closest') ? '!' : '').join('')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Stats panel — only AFL/NRL have rich live stats endpoint */}
      {!isSoccer && (
        <div className="px-3 pb-2">
          <MatchLiveStats
            sport={sport}
            homeTeam={homeName}
            awayTeam={awayName}
            roundNumber={game.round_number}
            defaultOpen={true}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Shows one rich live-game card per in-progress AFL/NRL/WC match.
 * Auto-refreshes every 30s. Renders nothing if no games are live.
 */
export default function LiveTicker() {
  const [aflLive, setAflLive] = useState([]);
  const [nrlLive, setNrlLive] = useState([]);
  const [wcLive, setWcLive] = useState([]);
  const [eplLive, setEplLive] = useState([]);
  const [tipStatus, setTipStatus] = useState({});
  const [wcLogos, setWcLogos] = useState({});
  const [eplLogos, setEplLogos] = useState({});
  const [syncTick, setSyncTick] = useState(0);           // forces re-render every second so "Xs ago" advances
  const lastFetchRef = useRef({ AFL: null, NRL: null, WC: null, EPL: null }); // local ticker baseline

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/teams/WC/logos`)
      .then(res => !cancelled && setWcLogos(res.data || {}))
      .catch(() => !cancelled && setWcLogos({}));
    axios.get(`${API_URL}/api/teams/EPL/logos`)
      .then(res => !cancelled && setEplLogos(res.data || {}))
      .catch(() => !cancelled && setEplLogos({}));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const [aflCR, nrlCR, wcCR, eplCR] = await Promise.all([
          axios.get(`${API_URL}/api/current-round/AFL`).catch(() => ({ data: null })),
          axios.get(`${API_URL}/api/current-round/NRL`).catch(() => ({ data: null })),
          axios.get(`${API_URL}/api/current-round/WC`).catch(() => ({ data: null })),
          axios.get(`${API_URL}/api/current-round/EPL`).catch(() => ({ data: null })),
        ]);
        const aflRound = aflCR?.data?.round_number;
        const nrlRound = nrlCR?.data?.round_number;
        const wcRound = wcCR?.data?.round_number;
        const eplRound = eplCR?.data?.round_number;
        const [aflRes, nrlRes, wcRes, eplRes, aflTips, nrlTips, wcTips, eplTips] = await Promise.all([
          axios.get(`${API_URL}/api/live-scores/afl${aflRound ? `?round_number=${aflRound}` : ''}`).catch(() => ({ data: { games: [] } })),
          axios.get(`${API_URL}/api/live-scores/nrl${nrlRound ? `?round_number=${nrlRound}` : ''}`).catch(() => ({ data: { games: [] } })),
          axios.get(`${API_URL}/api/live-scores/wc${wcRound ? `?round_number=${wcRound}` : ''}`).catch(() => ({ data: { games: [] } })),
          axios.get(`${API_URL}/api/live-scores/epl${eplRound ? `?round_number=${eplRound}` : ''}`).catch(() => ({ data: { games: [] } })),
          aflRound != null
            ? axios.get(`${API_URL}/api/live-tips-status?sport=AFL&round_number=${aflRound}`, { withCredentials: true }).catch(() => ({ data: {} }))
            : Promise.resolve({ data: {} }),
          nrlRound != null
            ? axios.get(`${API_URL}/api/live-tips-status?sport=NRL&round_number=${nrlRound}`, { withCredentials: true }).catch(() => ({ data: {} }))
            : Promise.resolve({ data: {} }),
          wcRound != null
            ? axios.get(`${API_URL}/api/live-tips-status?sport=WC&round_number=${wcRound}`, { withCredentials: true }).catch(() => ({ data: {} }))
            : Promise.resolve({ data: {} }),
          eplRound != null
            ? axios.get(`${API_URL}/api/live-tips-status?sport=EPL&round_number=${eplRound}`, { withCredentials: true }).catch(() => ({ data: {} }))
            : Promise.resolve({ data: {} }),
        ]);
        if (cancelled) return;
        setAflLive((aflRes.data?.games || []).filter(g => shouldShowAsLive(g, 'AFL')));
        setNrlLive((nrlRes.data?.games || []).filter(g => shouldShowAsLive(g, 'NRL')));
        setWcLive((wcRes.data?.games || []).filter(g => shouldShowAsLive(g, 'WC')));
        setEplLive((eplRes.data?.games || []).filter(g => shouldShowAsLive(g, 'EPL')));
        setTipStatus({ ...(aflTips.data || {}), ...(nrlTips.data || {}), ...(wcTips.data || {}), ...(eplTips.data || {}) });

        // Fetch sync status — drives "Updated Xs ago" indicator
        try {
          const ssRes = await axios.get(`${API_URL}/api/sync-status`);
          if (!cancelled) {
            // Anchor a local timestamp baseline so the per-second tick is accurate
            // between server fetches.
            const now = Date.now();
            const newBase = {};
            for (const sport of ['AFL', 'NRL', 'WC', 'EPL']) {
              const sec = ssRes.data?.[sport]?.seconds_ago;
              newBase[sport] = sec == null ? null : now - sec * 1000;
            }
            lastFetchRef.current = newBase;
            setSyncTick(t => t + 1); // trigger initial render with new baseline
          }
        } catch (_) { /* ignore */ }
      } catch (_) { /* swallow */ }
    };
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Per-second tick so the "Updated Xs ago" label advances live between server fetches.
  useEffect(() => {
    const id = setInterval(() => setSyncTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const total = aflLive.length + nrlLive.length + wcLive.length + eplLive.length;
  if (total === 0) return null;

  // Compute most-recent sync among live sports (so user sees the freshest cadence)
  const liveSports = [];
  if (aflLive.length) liveSports.push('AFL');
  if (nrlLive.length) liveSports.push('NRL');
  if (wcLive.length) liveSports.push('WC');
  if (eplLive.length) liveSports.push('EPL');
  const now = Date.now();
  // syncTick subscription — recompute every second
  const _tick = syncTick; void _tick;
  let freshestSecAgo = null;
  for (const sport of liveSports) {
    const base = lastFetchRef.current[sport];
    if (base == null) continue;
    const sec = Math.max(0, Math.floor((now - base) / 1000));
    if (freshestSecAgo == null || sec < freshestSecAgo) freshestSecAgo = sec;
  }
  const fmtAgo = (s) => {
    if (s == null) return '';
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
  };
  const isStale = freshestSecAgo != null && freshestSecAgo > 120; // >2min = warn

  return (
    <section className="mb-8" data-testid="live-ticker">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-[#34C759] animate-pulse" />
          <h2 className="text-xs uppercase tracking-wider font-bold text-[#1A1A1A]">
            Live now ({total})
          </h2>
        </div>
        {freshestSecAgo != null && (
          <div
            className={`text-[11px] font-mono tabular-nums ${isStale ? 'text-[#FF9500]' : 'text-gray-400'}`}
            title={isStale ? 'Live data is slightly behind — upstream API may be lagging' : 'Live score feed cadence'}
            data-testid="live-ticker-sync-status"
          >
            <span className="hidden sm:inline">Updated </span>{fmtAgo(freshestSecAgo)}
            {isStale ? ' ⚠' : ''}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {aflLive.map(g => <LiveCard key={`afl-${g.home_team}`} game={g} sport="AFL" tipStatus={tipStatus} />)}
        {nrlLive.map(g => <LiveCard key={`nrl-${g.home_team}`} game={g} sport="NRL" tipStatus={tipStatus} />)}
        {wcLive.map(g => <LiveCard key={`wc-${g.home_team}`} game={g} sport="WC" tipStatus={tipStatus} wcLogos={wcLogos} />)}
        {eplLive.map(g => <LiveCard key={`epl-${g.home_team}`} game={g} sport="EPL" tipStatus={tipStatus} eplLogos={eplLogos} />)}
      </div>
    </section>
  );
}
